import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedNumber — counts up from 0 to `value` when the element scrolls into view.
 * Accepts string values like "50+", "1M+", "99.7%", "7+" and animates only the
 * numeric portion while preserving suffix/prefix.
 */
export default function AnimatedNumber({ value, duration = 1400 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const playedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    // Parse: prefix non-digit, numeric portion (possibly with decimal), suffix non-digit
    const match = value.match(/^([^\d-]*)(-?\d+(?:\.\d+)?)(.*)$/);
    if (!match) { setDisplay(value); return; }
    const [, prefix, numStr, suffix] = match;
    const target = parseFloat(numStr);
    const decimals = (numStr.split('.')[1] || '').length;
    // If user prefers reduced motion, skip animation
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setDisplay(value); return; }

    // Handle 1M+ style — animate 0 → 1, but keep the M suffix
    // For values >= 1000 like "1M+", target is 1 already. Just animate normally.
    setDisplay(`${prefix}0${suffix}`);

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || playedRef.current) return;
        playedRef.current = true;
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - start) / duration);
          // ease-out cubic
          const eased = 1 - Math.pow(1 - t, 3);
          const current = target * eased;
          setDisplay(`${prefix}${current.toFixed(decimals)}${suffix}`);
          if (t < 1) requestAnimationFrame(tick);
          else setDisplay(value);
        };
        requestAnimationFrame(tick);
        obs.disconnect();
      });
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
}
